import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import Typography from '@mui/material/Typography';

import { IOfferEventDetailFragment } from 'graphql/types';
import { Chip } from 'common/components/Chip';
import { TOfferEventEnrollmentGroup } from 'offer/types';

interface IProps {
    groupEnrollmentCounts: IOfferEventDetailFragment['groupEnrollmentCounts'];
    selectedUsersCount?: number;
    value: string;
    onChange: (value: string) => void;
}

export const GroupConditionSelect = ({
    groupEnrollmentCounts,
    selectedUsersCount,
    value,
    onChange,
}: IProps) => {
    const [translate] = useTranslation();

    const isGroupDisabled = (max: number, count: number) => {
        if (!max) return false;

        if (count >= max) return true;

        return (
            selectedUsersCount !== undefined && selectedUsersCount > max - count
        );
    };

    const selectedGroup = groupEnrollmentCounts.find(
        ({ group }) => group.id === value
    );
    const hasError =
        !!selectedGroup &&
        !!selectedGroup.max &&
        selectedUsersCount !== undefined &&
        selectedGroup.count < selectedGroup.max &&
        selectedUsersCount > selectedGroup.max - selectedGroup.count;

    const renderGroupOption = (
        group: TOfferEventEnrollmentGroup,
        min: number,
        max: number,
        count: number
    ) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: 1,
            }}
        >
            <Typography component="span" variant="body1">
                {group.name}
            </Typography>

            {!!max && (
                <Chip
                    bgColor={count < min ? 'error' : 'success'}
                    label={translate('groupConditionSelect.spotsLeft', {
                        count,
                        max,
                    })}
                />
            )}
        </Box>
    );

    return (
        <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
            <InputLabel shrink>
                {translate('groupConditionSelect.label')}
            </InputLabel>

            <MuiSelect
                displayEmpty
                notched
                label={translate('groupConditionSelect.label')}
                renderValue={(selectedValue) => {
                    const enrollmentCount = groupEnrollmentCounts.find(
                        ({ group }) => group.id === selectedValue
                    );

                    if (!enrollmentCount)
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                    color="text.secondary"
                                    component="span"
                                    variant="body1"
                                >
                                    {translate('groupConditionSelect.none')}
                                </Typography>
                            </Box>
                        );

                    const { group, min, max, count } = enrollmentCount;

                    return renderGroupOption(group, min, max, count);
                }}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <MenuItem sx={{ py: 1.5 }} value="">
                    <Typography color="text.secondary" component="span">
                        {translate('groupConditionSelect.none')}
                    </Typography>
                </MenuItem>

                {groupEnrollmentCounts.map(({ group, min, max, count }) => (
                    <MenuItem
                        disabled={isGroupDisabled(max, count)}
                        key={group.id}
                        sx={{ py: 1.5 }}
                        value={group.id}
                    >
                        {renderGroupOption(group, min, max, count)}
                    </MenuItem>
                ))}
            </MuiSelect>

            {hasError && (
                <FormHelperText error role="alert">
                    {translate('groupConditionSelect.notEnoughSpots')}
                </FormHelperText>
            )}
        </FormControl>
    );
};
